import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { startOfDay, subMonths } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ActiveUsersTrendCard,
  AnalyticsToolbar,
  FillRateCard,
  GeographyCard,
  KpiStrip,
  LanguagesCard,
  PetsSpeciesCard,
  RegistrationFunnelCard,
  RegistrationsCard,
  UsersCountCard,
  VaccinationsCard,
  analyticsLib,
} from 'entities/analytics';

import { AnalyticsGranularity, AnalyticsVaccinationsType } from 'shared/api';

type AnalyticsTab = 'overview' | 'trends';

const isGranularity = (v: string | null): v is AnalyticsGranularity =>
  v === 'day' || v === 'week' || v === 'month' || v === 'half-year';

const isVaccinationsType = (v: string | null): v is AnalyticsVaccinationsType =>
  v === 'people' || v === 'pets' || v === 'all';

const isTab = (v: string | null): v is AnalyticsTab => v === 'overview' || v === 'trends';

export const Analytics = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<AnalyticsTab>(() => {
    const v = searchParams.get('tab');
    return isTab(v) ? v : 'overview';
  });
  const [from, setFrom] = useState<Date | null>(() => {
    const parsed = analyticsLib.fromApiDate(searchParams.get('from'));
    if (parsed) return parsed;
    return subMonths(startOfDay(new Date()), 6);
  });
  const [to, setTo] = useState<Date | null>(() => {
    const parsed = analyticsLib.fromApiDate(searchParams.get('to'));
    if (parsed) return parsed;
    return startOfDay(new Date());
  });
  const [granularity, setGranularity] = useState<AnalyticsGranularity>(() => {
    const v = searchParams.get('granularity');
    return isGranularity(v) ? v : 'month';
  });
  const [vaccinationsType, setVaccinationsType] = useState<AnalyticsVaccinationsType>(() => {
    const v = searchParams.get('type');
    return isVaccinationsType(v) ? v : 'all';
  });

  useEffect(() => {
    const next: Record<string, string> = { tab, granularity, type: vaccinationsType };
    const fromIso = analyticsLib.toApiDate(from);
    const toIso = analyticsLib.toApiDate(to);
    if (fromIso) next.from = fromIso;
    if (toIso) next.to = toIso;
    setSearchParams(next, { replace: true });
  }, [tab, from, to, granularity, vaccinationsType, setSearchParams]);

  const baseParams = useMemo(
    () => ({ from: analyticsLib.toApiDate(from), to: analyticsLib.toApiDate(to), granularity }),
    [from, to, granularity],
  );

  const handleTypeChange = useCallback((t: AnalyticsVaccinationsType) => setVaccinationsType(t), []);

  return (
    <Box display="flex" flexDirection="column" flex={1} px={3} py={2}>
      <Paper sx={{ mb: 2, px: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            if (isTab(v)) setTab(v);
          }}
        >
          <Tab value="overview" label="Overview" sx={{ textTransform: 'none', alignItems: 'flex-start' }} />
          <Tab value="trends" label="Trends" sx={{ textTransform: 'none', alignItems: 'flex-start' }} />
        </Tabs>
      </Paper>

      {tab === 'overview' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Current snapshot — activity, countries, languages, profile completeness and the registration funnel. Not
            affected by the date range.
          </Typography>
          <KpiStrip />
          <GeographyCard />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2 }}>
            <LanguagesCard />
            <PetsSpeciesCard />
          </Box>
          <FillRateCard />
          <RegistrationFunnelCard />
        </Box>
      )}

      {tab === 'trends' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Metrics over time. The date range below controls every chart on this tab.
          </Typography>
          <AnalyticsToolbar
            from={from}
            to={to}
            granularity={granularity}
            onFromChange={setFrom}
            onToChange={setTo}
            onGranularityChange={setGranularity}
          />
          <ActiveUsersTrendCard params={baseParams} />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
            }}
          >
            <RegistrationsCard params={baseParams} />
            <UsersCountCard params={baseParams} />
            <VaccinationsCard params={baseParams} type={vaccinationsType} onTypeChange={handleTypeChange} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
